'use client';

import axios from 'axios';
import { ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  EmptyAvatarImage,
  GradientAccountImage,
  GradientIEImage,
  GradientProductsImage,
  GradientStaffsImage,
  GradientStatisticsImage,
  GradientStoreFrontImage,
  GradientStoreImage,
} from '@/assets';
import useDebounce from '@/utils/useDebounce';

import CustomImage from '../common/CustomImage';

const slugIcons: { [key: string]: any } = {
  'quan-ly-tai-khoan': GradientAccountImage,
  'quan-ly-cua-hang': GradientStoreImage,
  'quan-ly-san-pham': GradientProductsImage,
  'quan-ly-nhan-vien': GradientStaffsImage,
  'quan-ly-ban-hang': GradientStoreFrontImage,
  'quan-ly-thu-chi': GradientIEImage,
  'quan-ly-thong-ke': GradientStatisticsImage,
};

export interface UserManualPageClientProps {
  title: string;
  content: string;
  topic: any;
  question: any;
  audit: any;
  menu: any;
}

export default function MainContent({
  title,
  content,
  topic,
  question,
  audit,
  menu,
}: UserManualPageClientProps) {
  const inputRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | undefined>();
  const [lastCompletedSearch, setLastCompletedSearch] = useState('');
  const search = useDebounce(input, 1000);
  const loading =
    !!search && search.length >= 3 && search !== lastCompletedSearch;
  const flattenDocs = menu?.reduce((current: string[], tp: any) => {
    current.push(`/docs/${tp.slug}`);
    current.push(...tp.questions.map((q: any) => `/docs/${tp.slug}/${q.slug}`));
    return current;
  }, []);

  useEffect(() => {
    if (search && search.length >= 3) {
      axios
        .post('/api/docs/search', { search })
        .then((resp) => {
          setSearchResults(resp.data);
          setLastCompletedSearch(search);
        })
        .catch(() => {
          setSearchResults([]);
          setLastCompletedSearch(search);
        });
    }
  }, [search]);

  const getFullDocSlug = (slug: string) => {
    return flattenDocs.find((doc: string) => doc.endsWith(slug)) ?? '/docs';
  };

  return (
    <div className="flex size-full flex-col">
      <div
        ref={inputRef}
        className="group relative my-2 box-border flex items-center rounded-lg border-2 focus-within:border-primary-500"
      >
        <Search className="mx-4 my-3 text-primary-950 group-focus-within:text-primary-500" />
        <input
          className="w-full py-2 text-primary-950"
          placeholder="Bạn đang muốn tìm kiếm thông tin gì?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        {loading && (
          <RefreshCw className="mx-4 my-3 size-6 h-6 animate-spin text-primary-950" />
        )}
        <div className="absolute -bottom-1 w-full">
          <div className="absolute left-0 top-0 z-40 hidden max-h-[70vh] w-full overflow-auto rounded-lg border bg-white shadow-md group-focus-within:block">
            {searchResults?.length === 0 ? (
              <>
                {search && search.length >= 3 && !loading && (
                  <p className="p-5 text-base">
                    Không tìm thấy kết quả mà bạn đang tìm kiếm
                  </p>
                )}
              </>
            ) : (
              <div>
                <ul>
                  {searchResults?.map((result: any) => (
                    <li key={result.slug}>
                      <div className="border-b p-5 hover:cursor-pointer hover:bg-primary-50">
                        <Link href={getFullDocSlug(result.slug)}>
                          <div>
                            <span className="text-primary-500 underline">
                              {result.title}
                            </span>
                          </div>
                          <div>
                            <span
                              className="text-sm text-primary-950"
                              dangerouslySetInnerHTML={{
                                __html: result.content,
                              }}
                            />
                            <span>...</span>
                          </div>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      {topic != null ? (
        <div className="mt-4">
          {question == null ? (
            <h1 className="py-4 text-left">
              <span className="text-primary-600">{title}</span> trong ứng dụng
              Zump FnB
            </h1>
          ) : (
            <div className="relative flex gap-5 border-b py-4">
              <CustomImage
                src={audit.authorAvatarUrl}
                fallback={EmptyAvatarImage}
                alt="author-avt"
                className="size-12 shrink-0 rounded-full border-2"
              />
              <div>
                <strong className="text-sm leading-5 text-primary-950">
                  {audit.authorName}
                </strong>
                <p className="text-sm leading-5 text-primary-950">{`Ngày đăng: ${moment(
                  audit.createdAt
                ).format('DD-MM-YYYY')} vào lúc ${moment(
                  audit.createdAt
                ).format('HH:mm')}`}</p>
              </div>
            </div>
          )}
          <div className="markdown py-4">
            <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
          </div>
          {question != null && <div className="my-10 border-t" />}
          <div>
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 font-medium text-primary-950">
                Bài viết liên quan
              </h3>
              <ul className="ml-2 space-y-3 [&_li]:mb-2">
                {topic.questions.map((q: any) => (
                  <li
                    key={q.slug}
                    className="transition-transform hover:translate-x-1"
                  >
                    <Link
                      href={`/docs/${topic.slug}/${q.slug}/`}
                      className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
                    >
                      <ChevronRight size={14} className="my-2 self-start" />
                      <span className="line-clamp-2">{q.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <h1 className="py-4 text-left">
            Chào mừng bạn đến với Sổ tay hướng dẫn sử dụng Zump FnB
          </h1>
          <div>
            <span className="text-lg text-primary-950">
              Dưới đây là một số giải pháp quản lý của chúng tôi
            </span>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {menu.map((tp: any) => (
                <Link key={tp.slug} href={`/docs/${tp.slug}/`}>
                  <div className="button-shinny group flex w-full items-center gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="relative size-12 shrink-0 rounded-full bg-primary-100 p-2 transition-transform duration-300 group-hover:scale-110">
                      <Image
                        src={slugIcons[tp.slug] ?? GradientStoreImage}
                        alt={tp.slug}
                      />
                    </div>
                    <div>
                      <div className="text-lg font-medium text-gray-950 group-hover:text-primary-600">
                        {tp.title}
                      </div>
                      <p className="text-left text-sm text-gray-500">
                        {tp.questions.length} bài viết
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mb-60 mt-16 grid grid-cols-1 gap-10 text-primary-950 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {menu.map((tp: any) => (
                <div
                  key={tp.slug}
                  className="rounded-lg border bg-white p-6 shadow-sm"
                >
                  <h3 className="mb-4 border-b pb-2 font-medium text-primary-950">
                    {tp.title}
                  </h3>
                  <ul className="ml-2 space-y-3 [&_li]:mb-2">
                    {tp.questions.map((q: any) => (
                      <li
                        key={q.slug}
                        className="transition-transform hover:translate-x-1"
                      >
                        <Link
                          href={`/docs/${tp.slug}/${q.slug}/`}
                          className="inline-flex gap-2 text-primary-600 hover:text-primary-700"
                        >
                          <ChevronLeft className="my-2 self-start" size={14} />
                          <span>{q.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
